import { type AgentSubscriber } from "@ag-ui/client";
import { ApplicationState } from "app/types-store";
import { ActionDispatching } from "app/types-shared";
import { api } from "./api";
import { actionCreators as caseCreateActionCreators } from "app/area/case/create/actions";
import { api as caseCreateApi } from "app/area/case/create/api";
import { CaseDraftParameter, CaseDraftPayload, CaseDraftSnapShot, RunAgUiArgs, SubscriberParams } 
from "app/components/ai-chat-assistant/types";
import {
  RequestAiAssistantRun,
  FailAiAssistantRun,
  FinishAiAssistantRun,
  ReceiveDraftConfirmation
} from "./action-types";

const extractConfirmedDraft = (event: { type: string, snapshot?: CaseDraftSnapShot }): CaseDraftPayload | undefined => {
  if (!event?.snapshot?.UserConfirmed) {
    return undefined;
  }
  return event?.snapshot?.CaseDraft;
};

const createSubscriber = (
  subscriber: AgentSubscriber,
  dispatch: (action) => void
): AgentSubscriber => {
  return {
    ...subscriber,
    onStateSnapshotEvent: (params: SubscriberParams) => {
      const draft = extractConfirmedDraft(params.event);
      if (draft) {
        dispatch(new ReceiveDraftConfirmation(draft));
      }
      subscriber.onStateSnapshotEvent?.(params as Parameters<NonNullable<typeof subscriber.onStateSnapshotEvent>>[0]);
    },
    onRunFailed: (params: SubscriberParams) => {
      const message = params.error?.message || '';
      dispatch(new FailAiAssistantRun(message));
      subscriber.onRunFailed?.(params as Parameters<NonNullable<typeof subscriber.onRunFailed>>[0]);
    },
    onRunFinishedEvent: async (params: SubscriberParams) => {
      dispatch(new FinishAiAssistantRun());
      await subscriber.onRunFinishedEvent?.(params as Parameters<NonNullable<typeof subscriber.onRunFinishedEvent>>[0]);
    }
  };
};

export const actionCreators = {
  createCaseDraft: (query: CaseDraftParameter[], caseTypeId: number): ActionDispatching<any, ApplicationState> =>
    async (dispatch) => {
      await dispatch(
        caseCreateActionCreators(caseCreateApi).createCase(caseTypeId, null, null, null, query)
      );
    },

  abortAiAssistantRun: (): ActionDispatching<any, ApplicationState> =>
    (dispatch) => {
      dispatch(new FinishAiAssistantRun());
    },

  executeAiAssistantRun: (args: RunAgUiArgs): ActionDispatching<any, ApplicationState> =>
    async (dispatch) => {
      dispatch(new RequestAiAssistantRun());

      try {
        await api.executeAiAssistantRun({
          ...args,
          subscriber: createSubscriber(args.subscriber, dispatch)
        });
      } catch (error: unknown) {
        const message = error instanceof Error ? error.message : String(error);
        dispatch(new FailAiAssistantRun(message));
      }
    }
};
